import React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import type { DateRange, OnSelectHandler } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DateRangePickerProps {
  readonly value?: DateRange;
  readonly onChange: OnSelectHandler<DateRange | undefined>;
  readonly id?: string;
  readonly label?: string;
  readonly placeholder?: string;
  readonly className?: string;
  readonly numberOfMonths?: number;
}

export default function DateRangePicker({
  value,
  onChange,
  id = "date-picker-range",
  label = "Date Range",
  placeholder = "Select date range",
  className,
  numberOfMonths = 2,
}: DateRangePickerProps) {
  const buttonText = React.useMemo(() => {
    if (!value?.from) {
      return placeholder;
    }

    if (value.to) {
      return `${format(value.from, "LLL dd, y")} - ${format(value.to, "LLL dd, y")}`;
    }

    return format(value.from, "LLL dd, y");
  }, [placeholder, value?.from, value?.to]);

  return (
    <Field className={cn("w-fit", className)}>
      {label.length ? <FieldLabel htmlFor={id}>{label}</FieldLabel> : null}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            id={id}
            className="rounded-full justify-start p-5 font-normal"
          >
            <CalendarIcon size={20} />
            <span>{buttonText}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            defaultMonth={value?.from}
            selected={value}
            onSelect={onChange}
            numberOfMonths={numberOfMonths}
          />
        </PopoverContent>
      </Popover>
    </Field>
  );
}
